import { useLayoutEffect } from 'react';
import { resolveRailOffset } from './railOffset.lib';

export const RAIL_OFFSET_VAR = '--export-rail-offset';

const readStickyTop = (el: Element): number => {
  const raw = getComputedStyle(el).getPropertyValue('--ah-sticky-top').trim();
  return raw.endsWith('px') ? parseFloat(raw) || 0 : 0;
};

/**
 * Keeps the sticky rail as tall as the viewport space below it, which shrinks
 * as the in-flow app header scrolls away. A flat 100vh cut off the bottom of
 * the rail's list; a fixed offset left a gap above it.
 *
 * Nothing about position is cached — a banner inserted above the row moves it
 * without resizing anything an observer watches. Written to a CSS variable
 * rather than state so a scroll frame doesn't re-render the sidebar.
 */
export const useRailOffset = (
  railRef: React.RefObject<HTMLElement>,
  enabled: boolean
): void => {
  useLayoutEffect(() => {
    const rail = railRef.current;
    const parent = rail?.parentElement;
    if (!enabled || !rail || !parent) return;

    let frame = 0;

    const apply = () => {
      frame = 0;
      const offset = resolveRailOffset({
        parentTop: parent.getBoundingClientRect().top,
        stickyTop: readStickyTop(parent),
      });
      rail.style.setProperty(RAIL_OFFSET_VAR, `${Math.round(offset)}px`);
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    apply();
    // Capture phase so scrolls of an inner container are seen on the way up.
    window.addEventListener('scroll', schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(parent);
    observer.observe(document.documentElement);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      rail.style.removeProperty(RAIL_OFFSET_VAR);
    };
  }, [railRef, enabled]);
};
